


import { useEffect, useState } from "react";
import config from '../../config';
import { ChevronDown, ChevronUp, Eye } from "lucide-react";
import './DueCus.css';
import PayModal from './PayModal';
import { useNavigate } from 'react-router-dom';
import DueComponent from "./dueComponent/DueComponent";
import React from 'react';

const ViewDue = () => {
  const columns = ['Customer Code', 'Customer', 'Customer Office', 'Total Amount', 'Total Paid Amount', 'Total Due Amount', 'Actions'];
  const expandedColumns = ['Invoice Number', 'Store', 'Sold Date', 'Total Amount', 'Paid Amount', 'Due Amount', 'Actions'];

  const [data, setData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Number of items per page

  const navigate = useNavigate();

  useEffect(() => {
    fetchDueCustomer();
  }, []);

  useEffect(() => {
    // Update filtered data whenever groupedData changes
    if (groupedData.length > 0) {
      const filtered = groupedData.filter(row => row.dueAmount > 0);
      setFilteredData(filtered);
      // Reset to first page when data changes
      setCurrentPage(1);
    }
  }, [groupedData]);

  const fetchDueCustomer = async () => {
    try {
      const response = await fetch(`${config.BASE_URL}/invoices`);
      if (!response.ok) {
        setError('Failed to fetch Sales Invoices');
        return;
      }
      const invoices = await response.json();

      const transactionPromises = invoices.map(async (invoice) => {
        const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
        if (transactionResponse.ok) {
          return await transactionResponse.json();
        }
        return [];
      });

      const transactionsData = await Promise.all(transactionPromises);

      const formattedData = invoices.map((invoice, index) => {
        const invoiceTransactions = transactionsData[index] || [];
        const creditTransactions = invoiceTransactions.filter(transaction => transaction.transactionType === 'credit');
        if (creditTransactions.length === 0 || !invoice.customer) return null;
      
        const transactionPaid = creditTransactions.reduce((total, transaction) => total + transaction.paid, 0);
        const transactionDue = creditTransactions.reduce((total, transaction) => total + transaction.due, 0);
        const transactionPrice = creditTransactions.reduce((total, transaction) => total + transaction.price, 0);
      
        return {
          invoiceId: invoice.invoiceId,
          invoiceNo: invoice.invoiceNo,
          customerCode: invoice.customer?.cusCode || "-", 
          customerName: invoice.customer?.cusName || "-",
          customerOffice: invoice.customer?.cusOffice || "-",
          cusId: invoice.customer?.cusId || "-", 
          store: invoice.store,
          date: invoice.invoiceDate,
          totalAmount: transactionPrice,
          paidAmount: transactionPaid,
          dueAmount: transactionDue,
        };
      }).filter(Boolean);

      setData(formattedData);
      updateGroupedData(formattedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateGroupedData = (currentData) => {
    const grouped = currentData.reduce((acc, item) => {
      const existing = acc.find(row => row.customerCode === item.customerCode);
      if (existing) {
        existing.totalAmount += Number(item.totalAmount);
        existing.paidAmount += Number(item.paidAmount);
        existing.dueAmount += Number(item.dueAmount);
      } else {
        acc.push({
          ...item,
          totalAmount: Number(item.totalAmount),
          paidAmount: Number(item.paidAmount),
          dueAmount: Number(item.dueAmount),
          cusOffice: item.cusOffice
        });
      }
      return acc;
    }, []);
    setGroupedData(grouped);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const toggleRowExpansion = (customerCode) => {
    if (expandedRows.includes(customerCode)) {
      setExpandedRows(expandedRows.filter(code => code !== customerCode));
    } else {
      setExpandedRows([...expandedRows, customerCode]);
    }
  };

  const handlePayClick = (invoice) => {
    setSelectedInvoice({
      ...invoice,
      cusId: invoice.customer?.cusId || "-" 
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInvoice(null);
  };

  const handleViewDetails = (cusId) => {
    if (!cusId || cusId === "-") {
      alert("Customer ID not available"); 
      return;
    }
    navigate(`/cus-due/${cusId}`);
  };

  return (
    <div>
      <div className="scrolling-container">
        <div className="new-sales-container">
          <div className='row mt-4'>
            <div className='col-md-3' style={{ paddingLeft: '50px' }}>
              <h4>Customers' Due</h4>
            </div>
            <div className='col-md-6 mb-4'>
              <DueComponent />
            </div>
          </div>

          {isLoading ? (
            <p>Loading...</p>
          ) : error ? (
            <p>Error: {error}</p>
          ) : null}

          <table className="due-cus-table">
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <th key={index}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentItems.map((row) => (
                <React.Fragment key={row.customerCode}>
                  <tr>
                    <td>{row.customerCode}</td>
                    <td>{row.customerName}</td>
                    <td>{row.customerOffice}</td>
                    <td>{row.totalAmount.toFixed(2)}</td>
                    <td>{row.paidAmount.toFixed(2)}</td>
                    <td>{row.dueAmount.toFixed(2)}</td>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        <button
                          className="btn btn-primary"
                          onClick={() => toggleRowExpansion(row.customerCode)}
                        >
                          {expandedRows.includes(row.customerCode) ? <ChevronUp /> : <ChevronDown />}
                        </button>
                        <button
                          className="btn btn-warning ms-2"
                          onClick={() => handleViewDetails(row.cusId)}
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedRows.includes(row.customerCode) && (
                    <tr className="expanded-row">
                      <td colSpan={columns.length}>
                        <table className="expanded-table">
                          <thead>
                            <tr>
                              {expandedColumns.map((column, index) => (
                                <th key={index}>{column}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {data
                              .filter(item => item.customerCode === row.customerCode)
                              .map((item, index) => (
                                <tr key={index}>
                                  <td>{item.invoiceNo}</td>
                                  <td>{item.store}</td>
                                  <td>{new Date(item.date).toLocaleString()}</td>
                                  <td>{item.totalAmount.toFixed(2)}</td>
                                  <td>{item.paidAmount.toFixed(2)}</td>
                                  <td>{item.dueAmount.toFixed(2)}</td>
                                  <td>
                                    <button
                                      className="btn btn-success"
                                      onClick={() => handlePayClick({
                                        ...item,
                                        customer: { cusId: row.cusId }
                                      })}
                                      disabled={item.dueAmount === 0}
                                    >
                                      Payment
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Pagination component */}
          {totalPages > 1 && (
            <div className="pagination justify-content-center mt-3 mb-3">
              <button
                className="btn btn-primary"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                Previous
              </button>
              
              {/* Show first page */}
              {currentPage > 2 && (
                <>
                  <span
                    className={`mx-1 px-2 py-1 border ${1 === currentPage ? 'border-primary fw-bold' : 'border-transparent'}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => handlePageChange(1)}
                  >
                    1
                  </span>
                  {currentPage > 3 && <span className="mx-1">...</span>}
                </>
              )}

              {/* Current page and surrounding pages */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page;
                if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return page <= totalPages ? (
                  <span
                    key={page}
                    className={`mx-1 px-2 py-1 border ${page === currentPage ? 'border-dark fw-bold' : 'border-transparent'}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </span>
                ) : null;
              })}

              {/* Show last page */}
              {currentPage < totalPages - 1 && (
                <>
                  {currentPage < totalPages - 2 && <span className="mx-1">...</span>}
                  <span
                    className={`mx-1 px-2 py-1 border ${totalPages === currentPage ? 'border-dark fw-bold' : 'border-transparent'}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => handlePageChange(totalPages)}
                  >
                    {totalPages}
                  </span>
                </>
              )}
              
              <button
                className="btn btn-primary"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          )}

          {showModal && selectedInvoice && (
            <PayModal
              show={showModal}
              handleClose={handleCloseModal}
              invoiceData={selectedInvoice}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewDue;

//v2 workng without pagnation

// import { useEffect, useState } from "react";
// import config from '../../config';
// import { ChevronDown, ChevronUp, Eye } from "lucide-react"; // Import Eye icon
// import './DueCus.css';
// import PayModal from './PayModal';
// import { useNavigate } from 'react-router-dom'; // Import useNavigate for navigation
// import DueComponent from "./dueComponent/DueComponent";

// const ViewDue = () => {
//   const columns = ['Customer Code', 'Customer', 'Customer Office', 'Total Amount', 'Total Paid Amount', 'Total Due Amount', 'Actions'];
//   const expandedColumns = ['Invoice Number', 'Store', 'Sold Date', 'Total Amount', 'Paid Amount', 'Due Amount', 'Actions'];

//   const [data, setData] = useState([]);
//   const [groupedData, setGroupedData] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [expandedRows, setExpandedRows] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [selectedInvoice, setSelectedInvoice] = useState(null);

//   const navigate = useNavigate(); // Initialize useNavigate

//   useEffect(() => {
//     fetchDueCustomer();
//   }, []);

//   const fetchDueCustomer = async () => {
//     try {
//       const response = await fetch(`${config.BASE_URL}/invoices`);
//       if (!response.ok) {
//         setError('Failed to fetch Sales Invoices');
//         return;
//       }
//       const invoices = await response.json();

//       const transactionPromises = invoices.map(async (invoice) => {
//         const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
//         if (transactionResponse.ok) {
//           return await transactionResponse.json();
//         }
//         return [];
//       });

//       const transactionsData = await Promise.all(transactionPromises);

//       // Fetch customer details for each invoice
//       const formattedData = invoices.map((invoice, index) => {
//         const invoiceTransactions = transactionsData[index] || [];
//         const creditTransactions = invoiceTransactions.filter(transaction => transaction.transactionType === 'credit');
//         if (creditTransactions.length === 0 || !invoice.customer) return null; // Ensure customer exists
      
//         const transactionPaid = creditTransactions.reduce((total, transaction) => total + transaction.paid, 0);
//         const transactionDue = creditTransactions.reduce((total, transaction) => total + transaction.due, 0);
//         const transactionPrice = creditTransactions.reduce((total, transaction) => total + transaction.price, 0);
      
//         return {
//           invoiceId: invoice.invoiceId,
//           invoiceNo: invoice.invoiceNo,
//           customerCode: invoice.customer?.cusCode || "-", 
//           customerName: invoice.customer?.cusName || "-",
//           customerOffice: invoice.customer?.cusOffice || "-",
//           cusId: invoice.customer?.cusId || "-", 
//           store: invoice.store,
//           date: invoice.invoiceDate,
//           totalAmount: transactionPrice,
//           paidAmount: transactionPaid,
//           dueAmount: transactionDue,
//         };
//       }).filter(Boolean);
      

//       setData(formattedData.filter(Boolean));
//       updateGroupedData(formattedData.filter(Boolean));
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const updateGroupedData = (currentData) => {
//     const grouped = currentData.reduce((acc, item) => {
//       const existing = acc.find(row => row.customerCode === item.customerCode);
//       if (existing) {
//         existing.totalAmount += Number(item.totalAmount);
//         existing.paidAmount += Number(item.paidAmount);
//         existing.dueAmount += Number(item.dueAmount);
//       } else {
//         acc.push({
//           ...item,
//           totalAmount: Number(item.totalAmount),
//           paidAmount: Number(item.paidAmount),
//           dueAmount: Number(item.dueAmount),
//           cusOffice: item.cusOffice // Include cusOffice here
//         });
//       }
//       return acc;
//     }, []);
//     setGroupedData(grouped);
//   };

//   const toggleRowExpansion = (customerCode) => {
//     if (expandedRows.includes(customerCode)) {
//       setExpandedRows(expandedRows.filter(code => code !== customerCode));
//     } else {
//       setExpandedRows([...expandedRows, customerCode]);
//     }
//   };

//   const handlePayClick = (invoice) => {
//     setSelectedInvoice({
//       ...invoice,
//       cusId: invoice.customer?.cusId || "-" 
//     });
//     setShowModal(true);
//   };
  

//   const handleCloseModal = () => {
//     setShowModal(false);
//     setSelectedInvoice(null);
//   };

//   // Function to handle eye icon click
//   const handleViewDetails = (cusId) => {
//     if (!cusId || cusId === "-") {
//       alert("Customer ID not available"); 
//       return;
//     }
//     navigate(`/cus-due/${cusId}`);
//   };

//   return (
//     <div>
//       <div className="scrolling-container">
//         <div className="new-sales-container">
//           {/* <h4>Due Customers</h4> */}

//           <div className='row mt-4'>
//             <div className='col-md-3' style={{ paddingLeft: '50px' }}>
//               <h4>Due Customers</h4>
//             </div >

//             {/* <div className='col-md-6 mb-4'>
//             <table className="table table-hover table-striped mb-0" style={{ width: '100px' }}>
//               <tbody className="bg-light">

//                 <tr>
//                   <td className="text-start ps-4 fw-bold">Total Debts</td>
//                   <td className="text-end pe-4 font-monospace">
//                     LKR  
//                   </td>
//                 </tr>

//                 <tr>
//                   <td className="text-start ps-4 fw-bold">Dated Cheques </td>
//                   <td className="text-end pe-4 font-monospace">
//                     LKR  
//                   </td>
//                 </tr>

//                 <tr>
//                   <td className="text-start ps-4 fw-bold">No. of unpaid invoices </td>
//                   <td className="text-end pe-4 font-monospace fw-bold text-success">
                     
//                   </td>
//                 </tr>

//               </tbody>
//             </table>
//           </div> */}

//             <div className='col-md-6 mb-4'>

//               <DueComponent />
//             </div>
//           </div>

//           {isLoading ? (
//             <p>Loading...</p>
//           ) : error ? (
//             <p>Error: {error}</p>
//           ) : null}

//           <table className="due-cus-table">
//             <thead>
//               <tr>
//                 {columns.map((column, index) => (
//                   <th key={index}>{column}</th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody>
//               {groupedData
//                 .filter(row => row.dueAmount > 0) // Filter rows with Total Due Amount > 0
//                 .map((row) => (
//                   <>
//                     <tr key={row.customerCode}>
//                       <td>{row.customerCode}</td>
//                       <td>{row.customerName}</td>
//                       <td>{row.customerOffice}</td> {/* Render cusOffice here */}
//                       <td>{row.totalAmount}</td>
//                       <td>{row.paidAmount}</td>
//                       <td>{row.dueAmount}</td>
//                       <td>
//                         <div className="d-flex justify-content-between align-items-center">
//                           <button
//                             className="btn btn-primary"
//                             onClick={() => toggleRowExpansion(row.customerCode)}
//                           >
//                             {expandedRows.includes(row.customerCode) ? <ChevronUp /> : <ChevronDown />}
//                           </button>
//                           <button
//                             className="btn btn-warning ms-2"
//                             onClick={() => handleViewDetails(row.cusId)} // Pass cusId to handleViewDetails
//                           >
//                             <Eye size={16} /> {/* Eye icon */}
//                           </button>
//                         </div>
//                       </td>
//                     </tr>
//                     {expandedRows.includes(row.customerCode) && (
//                       <tr className="expanded-row">
//                         <td colSpan={columns.length}>
//                           <table className="expanded-table">
//                             <thead>
//                               <tr>
//                                 {expandedColumns.map((column, index) => (
//                                   <th key={index}>{column}</th>
//                                 ))}
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {data
//                                 .filter(item => item.customerCode === row.customerCode)
//                                 .map((item, index) => (
//                                   <tr key={index}>
//                                     <td>{item.invoiceNo}</td>
//                                     <td>{item.store}</td>
//                                     <td>{new Date(item.date).toLocaleString()}</td>
//                                     <td>{item.totalAmount}</td>
//                                     <td>{item.paidAmount}</td>
//                                     <td>{item.dueAmount}</td>
//                                     <td>
//                                       <button
//                                         className="btn btn-success"
//                                         onClick={() => handlePayClick({
//                                           ...item,
//                                           customer: { cusId: row.cusId } // Include customer ID from the row data
//                                         })}
//                                         disabled={item.dueAmount === 0} // Disable button if Due Amount is 0
//                                       >
//                                         Payment
//                                       </button>
//                                     </td>
//                                   </tr>
//                                 ))}
//                             </tbody>
//                           </table>
//                         </td>
//                       </tr>
//                     )}
//                   </>
//                 ))}
//             </tbody>
//           </table>

//           {showModal && selectedInvoice && (
//             <PayModal
//               show={showModal}
//               handleClose={handleCloseModal}
//               invoiceData={selectedInvoice}
//             />
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ViewDue;
